import { api } from '@xtnd/web/shared/api';

// ======================
// EMAIL VERIFICATION TYPES
// ======================

export interface EmailVerificationResult {
  email: string;
  status: 'Success' | 'Pending' | 'Failed' | 'TemporaryFailure' | 'NotStarted';
  verificationToken?: string;
  lastVerifiedAt?: string;
}

export interface CreateVerificationTemplateDto {
  templateName?: string;
  fromEmail: string;
  subject?: string;
  successRedirectionURL?: string;
  failureRedirectionURL?: string;
  logoUrl?: string;
}

export interface VerificationTemplateConfig {
  templateName: string;
  fromEmail: string;
  subject: string;
  successRedirectionURL: string;
  failureRedirectionURL: string;
  htmlContent: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

// ======================
// EMAIL VERIFICATION SERVICE
// ======================

class EmailVerificationApi {
  private readonly baseSource = '/email';

  /**
   * Creates a custom verification email template for SES
   */
  async createVerificationTemplate(
    data: CreateVerificationTemplateDto
  ): Promise<VerificationTemplateConfig> {
    const response = await api.post<ApiResponse<VerificationTemplateConfig>>(
      `${this.baseSource}/verification/template`,
      data,
    );
    return response.data;
  }

  /**
   * Sends a verification email to the specified address (Public - no auth required)
   */
  async sendVerificationEmail(
    email: string,
    name?: string
  ): Promise<{ success: boolean; message: string; email: string; messageId: string; verificationLink: string }> {
    const response = await api.post<{ success: boolean; message: string; email: string; messageId: string; verificationLink: string }>(
      `${this.baseSource}/verify`,
      { email, name }
    );
    return response;
  }

  /**
   * Sends a verification email to the specified address (SES sender verification - requires auth)
   */
  async verifySenderEmail(
    email: string,
    templateName?: string
  ): Promise<EmailVerificationResult> {
    const response = await api.post<ApiResponse<EmailVerificationResult>>(
      `${this.baseSource}/verification/verify`,
      { email, templateName }
    );
    return response.data;
  }

  /**
   * Checks the verification status of an email address
   */
  async getVerificationStatus(email: string): Promise<EmailVerificationResult> {
    const response = await api.get<ApiResponse<EmailVerificationResult>>(
      `${this.baseSource}/verification/status/${encodeURIComponent(email)}`,
    );
    return response.data;
  }

  /**
   * Verify sender email (alternative direct method without ApiResponse wrapper)
   */
  async verifyEmail(email: string): Promise<any> {
    return api.post('/email/verification/verify', { email });
  }

  /**
   * Get verification status (alternative direct method)
   */
  async checkVerificationStatus(email: string): Promise<any> {
    return api.get(`/email/verification/status/${encodeURIComponent(email)}`);
  }

  /**
   * Deletes a custom verification template
   */
  async deleteVerificationTemplate(templateName: string): Promise<void> {
    await api.post<ApiResponse<void>>(
      `${this.baseSource}/verification/template/${encodeURIComponent(templateName)}/delete`,
    );
  }

  /**
   * Verifies and tracks an email address with status polling
   * Returns a promise that resolves when verification is complete or times out
   */
  async verifyEmailWithTracking(
    email: string,
    templateName?: string,
    options: {
      pollInterval?: number; // ms between status checks
      maxAttempts?: number; // max polling attempts
    } = {}
  ): Promise<EmailVerificationResult> {
    const { pollInterval = 5000, maxAttempts = 12 } = options; // Default: 5s interval, 1 minute timeout

    // Send verification email
    const initialResult = await this.verifySenderEmail(email, templateName);

    // If already verified or failed, return immediately
    if (initialResult.status === 'Success' || initialResult.status === 'Failed') {
      return initialResult;
    }

    // Poll for status updates
    let attempts = 0;
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      const status = await this.getVerificationStatus(email);

      if (status.status === 'Success' || status.status === 'Failed') {
        return status;
      }

      attempts++;
    }

    // Return the last known status if polling times out
    return await this.getVerificationStatus(email);
  }
}

export const emailVerificationApi = new EmailVerificationApi();
